import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightArrowLeft } from '@fortawesome/pro-light-svg-icons/faArrowRightArrowLeft';
import { TypedMemo } from '../../common/types';
import { AuthLogo } from '../../smartLinks/smlAuth/components/AuthLogo';
import { workspaceInitials } from '../../workspaces/workspaceInitials';
import { consentMarkHue } from './consentMarkHue';
import { OAuthConsentClientIdentity, OAuthConsentClientIdentityData } from './OAuthConsentClientIdentity';
import { oauthConsentMessages as messages } from './oauthConsentMessages';
import { withBrandWord } from '../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole consent page; each part uses its own slice of it */
import styles from './OAuthConsentView.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface OAuthConsentHeadProps {
  clientName: string;
  identity: OAuthConsentClientIdentityData;
  canWrite: boolean;
}

const markHues = [styles.hue0, styles.hue1, styles.hue2, styles.hue3, styles.hue4, styles.hue5];

/**
 * The top of the card (design: oauth-consent a1, `.oc-head`): Music24 and the app side by side,
 * what the app asks for, and who the app is. The app mark shows the initials of the client name;
 * a client has no logo that Music24 can trust.
 */
const OAuthConsentHeadComponent: React.FC<OAuthConsentHeadProps> = ({ clientName, identity, canWrite }) => {
  const intl = useIntl();
  return (
    <div className={styles.head}>
      <div className={styles.link} aria-hidden="true">
        <span className={styles.tile}>
          <AuthLogo size={36} />
        </span>
        <span className={styles.join}>
          <span className={styles.joinDot} />
          <span className={styles.joinDot} />
          <span className={styles.joinIcon}>
            <FontAwesomeIcon icon={faArrowRightArrowLeft} />
          </span>
          <span className={styles.joinDot} />
          <span className={styles.joinDot} />
        </span>
        <span className={clsx(styles.tile, styles.appTile, markHues[consentMarkHue(clientName)])}>
          {workspaceInitials(clientName)}
        </span>
      </div>
      <h1 className={styles.title}>
        {/* The client name comes from a third party: it goes in as an element, so withBrandWord styles
            only our own template text and a client named "MUSIC24 …" never looks like us. */}
        {withBrandWord(
          intl.formatMessage(canWrite ? messages.titleWrite : messages.titleRead, {
            app: <React.Fragment key="app">{clientName}</React.Fragment>,
          })
        )}
      </h1>
      <OAuthConsentClientIdentity identity={identity} />
    </div>
  );
};

export const OAuthConsentHead = TypedMemo(OAuthConsentHeadComponent);
